import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  Field,
  Input,
  Page,
  QueryState,
  Select,
  Stack,
  TextArea,
  Toggle,
} from '@integra/ui';
import {
  useCreateScimToken,
  useRevokeScimToken,
  useScimTokens,
  useSsoConnection,
  useUpsertSsoConnection,
} from '../api/hooks.js';
import { useAuthStore } from '../auth/store.js';
import type { WorkspaceRoleValue } from '../api/types.js';

const ROLES: WorkspaceRoleValue[] = ['viewer', 'editor', 'admin', 'owner', 'api_only'];

/**
 * Enterprise auth (§14.5): an organisation administrator configures SAML single
 * sign-on and SCIM provisioning here. SSO is dormant until it is enabled AND the
 * live SAML rollout is turned on for the organisation after a security review —
 * this screen manages the configuration and the SCIM provisioning tokens that an
 * identity provider (Okta, Entra ID, …) uses to push user lifecycle.
 */
export function SecurityPage() {
  const orgId = useAuthStore((s) => s.session?.organisationId ?? '');

  const connection = useSsoConnection(orgId);
  const saveConnection = useUpsertSsoConnection(orgId);

  const [idpEntityId, setIdpEntityId] = useState('');
  const [idpSsoUrl, setIdpSsoUrl] = useState('');
  const [idpCertificate, setIdpCertificate] = useState('');
  const [spEntityId, setSpEntityId] = useState('integra');
  const [defaultRole, setDefaultRole] = useState<WorkspaceRoleValue>('viewer');
  const [emailDomain, setEmailDomain] = useState('');
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const c = connection.data?.data;
    if (c) {
      setIdpEntityId(c.idpEntityId);
      setIdpSsoUrl(c.idpSsoUrl);
      setIdpCertificate(c.idpCertificate);
      setSpEntityId(c.spEntityId);
      setDefaultRole(c.defaultWorkspaceRole);
      setEmailDomain(c.emailDomain ?? '');
      setEnabled(c.enabled);
    }
  }, [connection.data]);

  function saveSso() {
    saveConnection.mutate({
      idpEntityId: idpEntityId.trim(),
      idpSsoUrl: idpSsoUrl.trim(),
      idpCertificate: idpCertificate.trim(),
      spEntityId: spEntityId.trim(),
      defaultWorkspaceRole: defaultRole,
      emailDomain: emailDomain.trim() === '' ? null : emailDomain.trim(),
      enabled,
    });
  }

  const tokens = useScimTokens(orgId);
  const createToken = useCreateScimToken(orgId);
  const revokeToken = useRevokeScimToken(orgId);
  const [tokenLabel, setTokenLabel] = useState('');
  const [freshToken, setFreshToken] = useState<string | null>(null);

  function generateToken() {
    const label = tokenLabel.trim();
    if (label === '') return;
    createToken.mutate(label, {
      onSuccess: (result) => {
        setFreshToken(result.data.token);
        setTokenLabel('');
      },
    });
  }

  return (
    <Page
      title="Security & SSO"
      description="SAML single sign-on and SCIM provisioning for your organisation. Configure your identity provider, then provision users automatically."
    >
      <Stack gap="lg">
        <QueryState
          loading={connection.isLoading}
          error={connection.error}
          what="the SSO configuration"
        >
          <Stack gap="lg">
            <Alert tone="info" title="How SSO rolls out">
              <ul className="ml-4 list-disc space-y-1 text-sm">
                <li>
                  Save your identity provider&rsquo;s SAML details below. The connection stays{' '}
                  <span className="font-semibold">dormant</span> until you enable it and the live
                  SAML rollout is switched on for your organisation after a security review.
                </li>
                <li>
                  SCIM lets your IdP create, update, and deactivate users automatically. Generate a
                  token below and paste it into your IdP&rsquo;s SCIM configuration.
                </li>
                <li>
                  Your SCIM base URL is your INTEGRA API host followed by{' '}
                  <span className="font-mono text-xs">/api/scim/v2</span>.
                </li>
              </ul>
            </Alert>

            <Card
              title="SAML single sign-on"
              subtitle="Your identity provider's SAML metadata. The signing certificate is a public key."
            >
              <Stack gap="md">
                <Field label="IdP entity ID (issuer)" htmlFor="sso-idp-entity">
                  <Input
                    id="sso-idp-entity"
                    value={idpEntityId}
                    placeholder="https://idp.example.com/entity"
                    onChange={(e) => setIdpEntityId(e.target.value)}
                  />
                </Field>
                <Field label="IdP SSO URL (sign-in endpoint)" htmlFor="sso-idp-url">
                  <Input
                    id="sso-idp-url"
                    value={idpSsoUrl}
                    placeholder="https://idp.example.com/sso/saml"
                    onChange={(e) => setIdpSsoUrl(e.target.value)}
                  />
                </Field>
                <Field label="IdP signing certificate (PEM)" htmlFor="sso-idp-cert">
                  <TextArea
                    id="sso-idp-cert"
                    rows={5}
                    value={idpCertificate}
                    placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                    onChange={(e) => setIdpCertificate(e.target.value)}
                  />
                </Field>
                <Cluster gap="md" align="start">
                  <Field label="Service-provider entity ID" htmlFor="sso-sp-entity">
                    <Input
                      id="sso-sp-entity"
                      value={spEntityId}
                      placeholder="integra"
                      onChange={(e) => setSpEntityId(e.target.value)}
                    />
                  </Field>
                  <Field label="Default workspace role for new users" htmlFor="sso-role">
                    <Select
                      id="sso-role"
                      value={defaultRole}
                      onChange={(e) => setDefaultRole(e.target.value as WorkspaceRoleValue)}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </Cluster>
                <Field label="Restrict to email domain (optional)" htmlFor="sso-domain">
                  <Input
                    id="sso-domain"
                    value={emailDomain}
                    placeholder="example.com"
                    onChange={(e) => setEmailDomain(e.target.value)}
                  />
                </Field>
                <Cluster justify="between" align="center" gap="md">
                  <Toggle
                    label="Enable SSO for this organisation"
                    checked={enabled}
                    onChange={setEnabled}
                  />
                  <Button loading={saveConnection.isPending} onClick={saveSso}>
                    Save SSO configuration
                  </Button>
                </Cluster>
                {saveConnection.isSuccess && (
                  <Alert tone="success" title="SSO configuration saved">
                    {enabled
                      ? 'Saved and enabled. It activates once the live SAML rollout is switched on for your organisation.'
                      : 'Saved. The connection is dormant until you enable it.'}
                  </Alert>
                )}
                {saveConnection.error instanceof Error && (
                  <Alert tone="error" title="Could not save the configuration">
                    {saveConnection.error.message}
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card
              title="SCIM provisioning tokens"
              subtitle="Bearer tokens your IdP uses to provision users. The secret is shown once, at creation."
            >
              <Stack gap="md">
                {freshToken !== null && (
                  <Alert tone="warning" title="Copy this token now — it will not be shown again">
                    <p className="break-all font-mono text-xs">{freshToken}</p>
                  </Alert>
                )}
                <Cluster gap="sm" align="end">
                  <Field label="New token label" htmlFor="scim-label">
                    <Input
                      id="scim-label"
                      value={tokenLabel}
                      placeholder="Okta production"
                      onChange={(e) => setTokenLabel(e.target.value)}
                    />
                  </Field>
                  <Button
                    variant="secondary"
                    loading={createToken.isPending}
                    onClick={generateToken}
                  >
                    Generate token
                  </Button>
                </Cluster>

                <QueryState loading={tokens.isLoading} error={tokens.error} what="the SCIM tokens">
                  {tokens.data && tokens.data.data.length === 0 ? (
                    <p className="text-sm text-ink-muted">No SCIM tokens yet.</p>
                  ) : (
                    <Stack gap="sm">
                      {tokens.data?.data.map((t) => (
                        <Cluster key={t.id} justify="between" align="center" gap="md">
                          <div className="min-w-0">
                            <Cluster gap="sm" align="center">
                              <span className="text-sm font-semibold">{t.label}</span>
                              {t.revokedAt !== null ? (
                                <Badge tone="neutral" dot>
                                  revoked
                                </Badge>
                              ) : (
                                <Badge tone="success" dot>
                                  active
                                </Badge>
                              )}
                            </Cluster>
                            <p className="mt-0.5 text-xs text-ink-muted">
                              Created {new Date(t.createdAt).toLocaleDateString()}
                              {t.lastUsedAt !== null
                                ? ` · last used ${new Date(t.lastUsedAt).toLocaleDateString()}`
                                : ' · never used'}
                            </p>
                          </div>
                          {t.revokedAt === null && (
                            <Button
                              size="sm"
                              variant="ghost"
                              loading={revokeToken.isPending}
                              onClick={() => revokeToken.mutate(t.id)}
                            >
                              Revoke
                            </Button>
                          )}
                        </Cluster>
                      ))}
                    </Stack>
                  )}
                </QueryState>
              </Stack>
            </Card>
          </Stack>
        </QueryState>
      </Stack>
    </Page>
  );
}
